import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const SN_CENTER = [-33.3358, -60.2136];
const SN_ZOOM = 13;
const RADIO_M = 260;

// Colores por categoría del modelo (mismos que la lista de Electoral).
const CAT_COLOR = {
  'competitivo · ganable': '#22c55e',
  'competitivo · defender': '#00b4d8',
  'base a movilizar': '#8b5cf6',
  'territorio rival': '#ef4444',
  'sin dato': '#64748b',
};
const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/^barrio\s+/, '').trim();

function popupHtml(nombre, e) {
  if (!e) return `<div style="font-family:system-ui;font-size:13px"><b>${nombre}</b><br/><span style="color:#64748b">Sin datos electorales</span></div>`;
  const nota = e.general
    ? `<span style="color:#f59e0b">Resultado general de San Nicolás (este barrio no tiene circuito propio asignado)</span>`
    : `<span style="color:#64748b">${(e.electores || 0).toLocaleString('es-AR')} electores · ${(e.circuitos || []).length} circuito(s) · ${e.gestion_resuelta || 0} gestiones</span>`;
  return `<div style="font-family:system-ui;font-size:13px;line-height:1.5">
    <b>${nombre}</b><br/>
    🗳️ LLA <b>${e.lla_share ?? '—'}%</b> · rival <b>${e.rival_share ?? '—'}%</b>${e.oportunidad != null ? `<br/>Oportunidad: <b>${e.oportunidad}/100</b> — ${e.categoria}` : `<br/>${e.categoria}`}<br/>
    ${nota}
  </div>`;
}

// Categoría a partir de shares LLA vs rival (0-100).
function catDe(lla, rival) {
  const m = (lla - rival) / 100;
  return lla >= rival ? (m > 0.15 ? 'base a movilizar' : 'competitivo · defender')
    : (m > -0.15 ? 'competitivo · ganable' : 'territorio rival');
}

export default function MapaElectoral({ barrios = [], porBarrio = [], resumen = null, onSelectBarrio }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layerRef = useRef(null);

  const conCoords = barrios.filter((b) => b.lat != null && b.lng != null);
  const byNombre = {};
  for (const e of porBarrio) byNombre[norm(e.barrio)] = e;

  // Fallback: resultado GENERAL de San Nicolás (real, agregado) para los barrios
  // que todavía no tienen un circuito asignado. Así el mapa nunca está vacío.
  let snDefault = null;
  if (resumen?.fuerzas?.length) {
    const lla = resumen.fuerzas.find((f) => /lla|libertad|chouhy/i.test(f.nombre));
    const rival = resumen.fuerzas.find((f) => !/lla|libertad|chouhy/i.test(f.nombre));
    const llaP = lla ? Math.round(lla.pct) : 0;
    const rivP = rival ? Math.round(rival.pct) : 0;
    snDefault = { lla_share: llaP, rival_share: rivP, categoria: catDe(llaP, rivP), general: true, electores: resumen.electores, circuitos: [] };
  }

  useEffect(() => {
    if (mapObj.current || !mapRef.current) return;
    if (!document.getElementById('barrio-label-style')) {
      const st = document.createElement('style');
      st.id = 'barrio-label-style';
      st.textContent = '.barrio-label{background:transparent;border:none;box-shadow:none;color:#0b1320;font-weight:700;font-size:11px;text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff}.barrio-label::before{display:none}';
      document.head.appendChild(st);
    }
    const map = L.map(mapRef.current, { scrollWheelZoom: true }).setView(SN_CENTER, SN_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapObj.current = map;
    setTimeout(() => map.invalidateSize(), 60);
    return () => { map.remove(); mapObj.current = null; };
  }, []);

  useEffect(() => {
    const map = mapObj.current, group = layerRef.current;
    if (!map || !group) return;
    group.clearLayers();
    conCoords.forEach((b) => {
      const real = byNombre[norm(b.nombre)];
      const e = real || snDefault;           // fallback: resultado general de SN
      const color = e ? (CAT_COLOR[e.categoria] || '#64748b') : '#334155';
      const area = L.circle([b.lat, b.lng], {
        radius: RADIO_M, color, weight: real ? 3 : 1.5,
        // Barrio con circuito propio = opacidad plena; con default general = tenue.
        fillColor: color, fillOpacity: real ? 0.5 : (e ? 0.22 : 0.1),
        dashArray: real ? null : '3',
      });
      area.bindTooltip(b.nombre, { direction: 'top', className: 'barrio-label', sticky: true });
      if (onSelectBarrio) area.on('click', () => onSelectBarrio(b));
      else area.bindPopup(popupHtml(b.nombre, e));
      group.addLayer(area);
    });
    setTimeout(() => map.invalidateSize(), 30);
  }, [barrios, porBarrio, onSelectBarrio]); // eslint-disable-line react-hooks/exhaustive-deps

  const conDato = porBarrio.filter((e) => e.barrio && e.barrio !== 'Sin barrio').length;

  return (
    <div>
      <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
        <span style={{ color: '#22c55e' }}>●</span> ganable ·{' '}
        <span style={{ color: '#00b4d8' }}>●</span> defender ·{' '}
        <span style={{ color: '#8b5cf6' }}>●</span> base propia ·{' '}
        <span style={{ color: '#ef4444' }}>●</span> territorio rival · &nbsp;
        <b>lleno</b> = circuito propio · <b>punteado</b> = resultado general de SN
      </div>
      <div ref={mapRef} style={{ height: 460, width: '100%', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }} />
      {snDefault && conDato < conCoords.length && (
        <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          Los barrios punteados muestran el resultado <b>general de San Nicolás</b> (dato real) hasta que les asignes su circuito — clickeá un barrio para asignarlo.
        </div>
      )}
    </div>
  );
}
